import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  EditIcon,
  ArrowLeftIcon,
  MapPinIcon,
  UsersIcon,
  Flashlight,
  BookText,
  DollarSign,
  FileText,
  ClipboardList,
  MessageCircle,
  Wrench,
  Eye,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTerritoryMapping, useTerritoryPremiseStats } from "../../../hooks";
import { TerritoryMappingItem, EntityFilters } from "../../../types";
import EditEntityModal from "../edit/EditEntityModal";
import { EntityFilterPanel } from "./EntityFilterPanel";
import { PAGE_SIZE } from "@shared/utils/constants";

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  bgColor: string;
}

const StatCard = ({ icon, label, value, bgColor }: StatCardProps) => (
  <div className={` rounded-md px-6 py-5 border  w-full`}>
    <div className="flex items-center space-x-3">
      <div className=" bg-opacity-20 p-2 rounded-full">{icon}</div>
      <div>
        <div className="card-subheading-text">{label}</div>
        <div className="card-value-text">{Number(value).toLocaleString()}</div>
      </div>
    </div>
  </div>
);

interface EntityDetailViewProps {
  result?: any;
  entityData?: any;
  onBack?: () => void;
}

const EntityDetailView: React.FC<EntityDetailViewProps> = ({
  result,
  entityData: propEntityData,
  onBack,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = PAGE_SIZE;
  const { code } = useParams<{ code: string }>();
  const decodedCode = decodeURIComponent(code || "");
  const tab = decodeURIComponent(searchParams.get("tab") || "");
  console.log(" Tab Decoded Code from URL:", tab);

  // Get entity data from multiple sources with proper fallbacks
  const { data: entityData } = location.state || {};
  const resultEntityData = result;

  const initialEntity = propEntityData || entityData || resultEntityData || {};

  // ✅ FIXED: Store the entity code in state so it doesn't become undefined
  const [entityCode, setEntityCode] = useState<string>(() => {
    return initialEntity?.code || decodedCode || "";
  });

  useEffect(() => {
    const newCode = propEntityData?.code || entityData?.code || result?.code;
    if (newCode && newCode !== entityCode) {
      setEntityCode(newCode);
    }
  }, [propEntityData?.code, entityData?.code, result?.code, entityCode]);

  //  Update entity code when URL params change
  useEffect(() => {
    if (decodedCode && decodedCode !== entityCode) {
      console.log(
        "URL changed, updating entityCode from:",
        entityCode,
        "to:",
        decodedCode
      );
      setEntityCode(decodedCode);
    }
  }, [decodedCode]);

  // ✅ UPDATE: Use useCallback for getFiltersFromUrl
  const getFiltersFromUrl = useCallback((): EntityFilters => {
    const page = parseInt(searchParams.get("page") || "1");

    const urlFilters: EntityFilters = {
      page,
      limit: itemsPerPage,
      territory_code: entityCode,
    };

    const territoryTypeParam = searchParams.get("territoryType");
    if (territoryTypeParam) {
      urlFilters.territoryType = territoryTypeParam;
    }

    const statusParam = searchParams.get("status");
    if (statusParam) {
      urlFilters.status = statusParam === "true";
    }

    const createdOnParam = searchParams.get("createdOn");
    if (createdOnParam) {
      urlFilters.createdOn = createdOnParam;
    }

    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  }, [searchParams, entityCode, itemsPerPage]);

  const [filters, setFilters] = useState<EntityFilters>(getFiltersFromUrl);
  const [entity, setEntity] = useState(initialEntity);

  // Update filters when URL changes or entityCode changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [getFiltersFromUrl]);

  const { data: territoryData, isLoading: loading } = useTerritoryMapping(
    filters.page || 1,
    filters.limit || itemsPerPage,
    entityCode,
    filters.search_data,
    "1",
    filters
  );

  const handleEntityUpdate = (updatedEntity: any) => {
    setEntity(updatedEntity);
  };

  const handleBackClick = () => {
    navigate(`/utility/utility-setup/service-area?tab=${tab}`);
  };

  const handleApplyFilters = (newFilters: Partial<EntityFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("territoryType");
    params.delete("status");
    params.delete("createdOn");
    params.delete("page");

    // Apply the filters with correct parameter names
    if (newFilters.territoryType) {
      params.set("territoryType", newFilters.territoryType);
    }
    if (newFilters.status !== undefined) {
      params.set("status", newFilters.status.toString());
    }
    if (newFilters.createdOn) {
      params.set("createdOn", newFilters.createdOn);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("territoryType");
    params.delete("status");
    params.delete("createdOn");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string) => {
    console.log(`Search term: "${searchTerm}", Param: "${paramName}"`);

    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    setSearchParams(params);
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // const fieldRows = [
  //   [
  //     { label: "Status", value: entity.isActive ? "Active" : "In Active" },
  //     { label: "Created By", value: entity.createdBy || "N/A" },
  //     { label: "Last Updated", value: entity.createdDate || "N/A" },
  //     { label: "Child Entity Count", value: entity.childCount || 0 },
  //     { label: "Entity Type", value: entity.territoryType || "Region" },
  //   ],
  //   [],
  // ];

  // ✅ FIXED: Use stable entityCode
  const { data: statsData, isLoading: isStatsLoading } =
    useTerritoryPremiseStats(entityCode);
  const entityTypeDisplay = statsData?.basicInfo.entityTypeDisplay ;

  const fieldRows = [
    [
      {
        label: "Status",
        value: statsData?.basicInfo.isActive ? "Active" : "In Active",
      },
      { label: "Created By", value: statsData?.basicInfo.createdBy || "N/A" },
      {
        label: "Last Updated",
        value: statsData?.basicInfo.createdDate || "N/A",
      },
      {
        label: "Child Entity Count",
        value: statsData?.basicInfo.childCount || 0,
      },
      { label: "Entity Type", value: statsData?.basicInfo.entityTypeDisplay || "NA" },
    ],
    [],
  ];

  const territoryColumns: TableColumn<TerritoryMappingItem>[] = [
    { key: "name", header: "Name", type: "text" },
    { key: "configurationCode", header: "Code", type: "text" },
    {
      key: "territoryType",
      header: "Territory Type",
      type: "custom",
      render: (value: string) => (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          {value}
        </Badge>
      ),
    },
    {
      key: "totalUnits",
      header: "Total Units",
      width: "w-24",
      type: "custom" as const,
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value}</span>
      ),
    },
    {
      key: "consumerCount",
      header: "Consumer Count",
      width: "w-24",
      type: "custom" as const,
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value}</span>
      ),
    },
    {
      key: "meterCount",
      header: "Meter Count",
      width: "w-24",
      type: "custom" as const,
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value}</span>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      type: "custom",
      render: (value: boolean) => (
        <Badge
          className={
            value
              ? "bg-green-100 text-green-800 border-green-200"
              : "bg-red-100 text-red-800 border-red-200"
          }
        >
          {value ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    { key: "createdBy", header: "Created By", type: "text" },
    { key: "createdDate", header: "Created On", type: "text" },

    {
      key: "actions",
      header: "Actions",
      sortable: false,
      width: "w-32",
      type: "custom",
      render: (_: any, row: any) => (
        <div className="flex gap-2">
          <button
            onClick={() =>
              navigate(
                `/utility/utility-setup/service-area/view/${encodeURIComponent(
                  row.code
                )}?tab=${tab}`
              )
            }
            className="text-blue-600 hover:text-blue-900 transition-colors"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div className="min-h-screen">
      <div className="space-y-4 p-4">
        <div className="flex justify-start">
          <Button
            variant="outline"
            onClick={handleBackClick}
            className="bg-white/80 backdrop-blur-sm border-blue-200 text-blue-700 hover:bg-blue-50 h-9 px-4 shadow-md"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2"/>
            Go Back
          </Button>
        </div>

        {/* Header */}
        <div className=" rounded-md py-4 pr-4 border">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-full">
                  <MapPinIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold mb-1">
                    {statsData?.basicInfo.name || "NA"}
                  </h1>
                  <p className="text-blue-500 text-sm font-medium">
                    {entity?.description || ""}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="bg-blue-50 text-blue-500 border-blue-600 border border-white/30 text-xs">
                      Code: {entity?.configurationCode || "N/A"}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
            <Button
              onClick={() => setIsEditModalOpen(true)}
              className="bg-white text-blue-600 hover:bg-blue-50 border px-4 py-2 h-9"
            >
              <EditIcon className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        {/* Basic Info */}
        <Card className="border rounded-md">
          <CardHeader className="rounded-t-lg pb-3">
            <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <div className="bg-blue-100 p-1.5 rounded-lg">
                <UsersIcon className="w-4 h-4 text-blue-600" />
              </div>
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-4">
              {fieldRows.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                  {row.map((field, fieldIndex) => (
                    <div
                      key={fieldIndex}
                      className="p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200"
                    >
                      <span className="card-subheading-text uppercase">
                        {field.label}
                      </span>
                      <div className="text-sm capitalize mt-1">
                        {String(field.value)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {entity.tags && entity.tags.length > 0 && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200">
                  <span className="text-sm font-semibold text-gray-800 mb-2 block">
                    Tags
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {entity.tags.map((tag: string, index: number) => (
                      <Badge
                        key={index}
                        className=" px-2 py-0.5 text-xs font-medium hover:shadow-lg transition-all duration-200"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stat Cards Section */}
        {statsData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<UsersIcon size={20} />}
              label="Consumers"
              value={statsData.consumerData.consumerCount}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<Flashlight size={20} />}
              label="Meters"
              value={statsData.meterData.meterCount}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<BookText size={20} />}
              label="Meter Readings"
              value={statsData.meterData.meterReadingCount}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<DollarSign size={20} />}
              label="Revenue"
              value={`${statsData.consumerData.totalRevenue}`}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<FileText size={20} />}
              label="Bills"
              value={statsData.billData.billCount}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<ClipboardList size={20} />}
              label="Service Requests"
              value={statsData.consumerData.serviceCount}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<MessageCircle size={20} />}
              label="Complaints"
              value={statsData.consumerData.complaintCount}
              bgColor="bg-white-500"
            />
            <StatCard
              icon={<Wrench size={20} />}
              label="Work Orders"
              value={statsData.serviceOrderData.jobCardCount}
              bgColor="bg-white-500"
            />
          </div>
        )}

        {/* Territory Mapping Table-->It should not visible in case of premise tab and entity type value 10 which is of premise */}
        {tab !== "premises" && entityTypeDisplay !== 'Premise' && (
          <Card className=" border">
            <CardHeader className="rounded-t-lg pb-3">
              <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <div className="bg-green-100 p-1.5 rounded-lg">
                  <MapPinIcon className="w-4 h-4 text-green-600" />
                </div>
                Child Entities ({territoryData?.count || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <GenericTable
                data={territoryData?.result || []}
                columns={territoryColumns}
                searchPlaceholder="Search entity..."
                searchParamName="query"
                searchDebounceMs={300}
                onSearch={handleSearch}
                rowKey="code"
                pagination={{
                  currentPage: currentPage,
                  totalPages: Math.ceil(
                    (territoryData?.count || 0) / itemsPerPage
                  ),
                  totalItems: territoryData?.count || 0,
                  itemsPerPage,
                  startItem: (currentPage - 1) * itemsPerPage + 1,
                  endItem: Math.min(
                    currentPage * itemsPerPage,
                    territoryData?.count || 0
                  ),
                }}
                onPageChange={handlePageChange}
                filterPanel={
                  <EntityFilterPanel
                    currentFilters={filters}
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={handleResetFilters}
                  />
                }
                emptyMessage="No Child Entities data found"
                actionsType="dropdown"
              />
            </CardContent>
          </Card>
        )}
      </div>

      <EditEntityModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        entity={entity}
        entityType={entity?.entityTypeDisplay || "Region"}
        onUpdate={handleEntityUpdate}
      />
    </div>
  );
};

export default EntityDetailView;
